import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Briefcase, Plus, Download, Loader, ChevronRight, ChevronLeft, Trash2, Pencil, LayoutGrid, List, User, FileSpreadsheet } from 'lucide-react';
import { DescriptionBox } from '../common/DescriptionBox';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { SearchableSelect } from '../common/SearchableSelect';
import { ContactAutocompleteMultiSelect } from '../common/ContactAutocompleteMultiSelect';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useNavigation } from '../../contexts/NavigationContext';
import { useAuth } from '../../contexts/AuthContext';
import { ProjectDetail } from './ProjectDetail';
import { ClientConstructionProjectView } from './ClientConstructionProjectView';

interface ConstructionProject {
  _id: string;
  project_ref: string;
  project_name: string;
  country_code: string;
  contract_value: number;
  client_org_id?: { _id?: string; companyName: string };
  client_contact_id?: { _id: string; name: string; email?: string };
  client_contact_ids?: Array<{ _id: string; name: string; email?: string }>;
  main_contractor_org_id?: { _id?: string; companyName: string };
  status?: string;
  my_role?: 'client' | 'contractor' | 'both';
  client_accepted_at?: string; // ISO date; when set, direct editing is blocked; use variations
}

interface Account {
  _id: string;
  companyName: string;
}

interface Contact {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  isClient?: boolean;
}

export const ConstructionProjects: React.FC = () => {
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const { t } = useTranslation();
  const { navigate, currentPath } = useNavigation();
  const { user } = useAuth();
  const [projects, setProjects] = useState<ConstructionProject[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [accountCountries, setAccountCountries] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table');
  const [roleFilter, setRoleFilter] = useState<'all' | 'client' | 'contractor'>('all');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const isProjectAccepted = (p: ConstructionProject) => !!p.client_accepted_at;

  const filteredProjects = projects.filter((p) => {
    if (roleFilter === 'all') return true;
    if (roleFilter === 'client') return p.my_role === 'client' || p.my_role === 'both';
    if (roleFilter === 'contractor') return p.my_role === 'contractor' || p.my_role === 'both';
    return true;
  });

  const pathParts = currentPath.split('/').filter(Boolean);
  const isClientView = pathParts[0] === 'projects' && pathParts[1] && pathParts[2] === 'client-view';
  const projectId = pathParts[0] === 'projects' && pathParts[1] && pathParts[1] !== 'client-view'
    ? (isClientView ? pathParts[1] : pathParts[1])
    : null;
  const isDetailView = projectId && pathParts[0] === 'projects' && pathParts[1] && !isClientView;

  useEffect(() => {
    setCustomBreadcrumbs([
      { label: t('navigation.home') || 'Home', path: '/', icon: 'Home' },
      { label: t('navigation.projects') || 'Projects', path: '/projects', icon: 'Briefcase' },
    ]);
    return () => setCustomBreadcrumbs(null);
  }, [t, setCustomBreadcrumbs]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/construction/projects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (data.success && data.data) {
        setProjects(data.data);
      } else {
        setError(data.message || 'Failed to load projects');
      }
    } catch (err) {
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  const fetchAccounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      let res = await fetch(`${apiUrl}/api/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      let data = await res.json();
      if (data.success && data.data?.length) {
        setAccounts(Array.isArray(data.data) ? data.data : [data.data] || []);
        return;
      }
      res = await fetch(`${apiUrl}/api/users/me/accounts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      data = await res.json();
      if (data.success && data.data?.length) {
        setAccounts(data.data.map((a: { accountId: string; companyName: string }) => ({
          _id: a.accountId,
          companyName: a.companyName,
        })));
      }
    } catch {
      setAccounts([]);
    }
  }, [apiUrl]);

  const fetchContacts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/contacts?type=client&limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success && data.data?.length) {
        setContacts(data.data.map((c: { _id: string; name: string; email?: string; phone?: string }) => ({
          _id: c._id,
          name: c.name,
          email: c.email,
          phone: c.phone,
        })));
      } else {
        setContacts([]);
      }
    } catch {
      setContacts([]);
    }
  }, [apiUrl]);

  const fetchAccountCountries = useCallback(async () => {
    const token = localStorage.getItem('token');
    const accountId = user?.currentAccountId;
    if (!token || !accountId) return;
    try {
      const [accountRes, countriesRes] = await Promise.all([
        fetch(`${apiUrl}/api/accounts/${accountId}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiUrl}/api/construction/countries`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      const accountData = await accountRes.json();
      const countriesData = await countriesRes.json();
      const enabled = accountData.account?.countries_enabled as string[] | undefined;
      const allCountries = (countriesData.data || []) as { code: string; name: string }[];
      if (enabled?.length) {
        setAccountCountries(allCountries.filter((c) => enabled.includes(c.code)));
      } else {
        setAccountCountries(allCountries);
      }
    } catch {
      setAccountCountries([]);
    }
  }, [apiUrl, user?.currentAccountId]);

  useEffect(() => {
    fetchProjects();
    fetchAccounts();
    fetchContacts();
    fetchAccountCountries();
  }, [fetchProjects, fetchAccounts, fetchContacts, fetchAccountCountries]);

  const downloadReport = async (pid: string, projectRef: string) => {
    const token = localStorage.getItem('token');
    const response = await fetch(
      `${apiUrl}/api/construction/projects/${pid}/reports/retention.pdf`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `retention-${projectRef}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const goToProject = (id: string) => {
    navigate(`/projects/${id}`);
  };

  const goBack = () => {
    navigate('/projects');
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProjects.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredProjects.map((p) => p._id)));
  };

  const deleteProject = async (id: string) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const res = await fetch(`${apiUrl}/api/construction/projects/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success) {
      setDeleteConfirmId(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      fetchProjects();
    } else {
      alert(data.message || 'Failed to delete project');
    }
  };

  const executeBulkDelete = async () => {
    const count = selectedIds.size;
    if (count === 0) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const ids = Array.from(selectedIds);
    let ok = 0;
    for (const id of ids) {
      const res = await fetch(`${apiUrl}/api/construction/projects/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) ok++;
    }
    setSelectedIds(new Set());
    setBulkDeleteConfirmOpen(false);
    fetchProjects();
    if (ok < count) alert(`Deleted ${ok} of ${count} projects.`);
  };

  if (isClientView && projectId) {
    return (
      <ClientConstructionProjectView
        projectId={projectId}
        onBack={() => navigate('/projects')}
      />
    );
  }

  if (isDetailView && projectId) {
    return (
      <ProjectDetail
        projectId={projectId}
        onBack={goBack}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader className="w-8 h-8 animate-spin text-accent-600" />
      </div>
    );
  }

  const clientCount = projects.filter((p) => p.my_role === 'client' || p.my_role === 'both').length;
  const contractorCount = projects.filter((p) => p.my_role === 'contractor' || p.my_role === 'both').length;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">{t('navigation.projects') || 'Projects'}</h1>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className={`p-2 ${viewMode === 'table' ? 'bg-accent-600 text-white' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
              title="Table view"
            >
              <List className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setViewMode('card')}
              className={`p-2 ${viewMode === 'card' ? 'bg-accent-600 text-white' : 'bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600'}`}
              title="Card view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => setShowCreateProject(true)}
            className="inline-flex items-center px-4 py-2 bg-accent-600 text-white rounded-lg text-sm font-medium hover:bg-accent-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Project
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-sm text-gray-600 dark:text-gray-400">View:</span>
        <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
          <button
            type="button"
            onClick={() => setRoleFilter('all')}
            className={`px-4 py-2 text-sm font-medium ${
              roleFilter === 'all'
                ? 'bg-accent-600 text-white'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
          >
            All ({projects.length})
          </button>
          <button
            type="button"
            onClick={() => setRoleFilter('client')}
            className={`px-4 py-2 text-sm font-medium ${
              roleFilter === 'client'
                ? 'bg-accent-600 text-white'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
          >
            Client ({clientCount})
          </button>
          <button
            type="button"
            onClick={() => setRoleFilter('contractor')}
            className={`px-4 py-2 text-sm font-medium ${
              roleFilter === 'contractor'
                ? 'bg-accent-600 text-white'
                : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600'
            }`}
          >
            Contractor ({contractorCount})
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="mb-4 flex items-center gap-3 p-3 bg-accent-50 dark:bg-accent-900/20 rounded-lg border border-accent-200 dark:border-accent-800">
          <span className="text-sm font-medium text-accent-800 dark:text-accent-200">
            {selectedIds.size} selected
          </span>
          {(() => {
            const firstId = Array.from(selectedIds)[0];
            const firstProject = firstId ? projects.find((p) => p._id === firstId) : null;
            const canEdit = firstProject && !isProjectAccepted(firstProject);
            return (
              <button
                onClick={() => canEdit && setEditProjectId(firstId ?? null)}
                disabled={!canEdit}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm border rounded ${
                  canEdit
                    ? 'border-accent-300 dark:border-accent-600 hover:bg-accent-100 dark:hover:bg-accent-800/40'
                    : 'border-gray-300 dark:border-gray-600 opacity-50 cursor-not-allowed'
                }`}
                title={!canEdit ? 'Project accepted by client – use variations to make changes' : 'Edit'}
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            );
          })()}
          <button
            onClick={() => setBulkDeleteConfirmOpen(true)}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Clear
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {viewMode === 'table' ? (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={filteredProjects.length > 0 && selectedIds.size === filteredProjects.length}
                    onChange={() => {
                      if (selectedIds.size === filteredProjects.length) {
                        setSelectedIds(new Set());
                      } else {
                        setSelectedIds(new Set(filteredProjects.map((p) => p._id)));
                      }
                    }}
                    className="rounded border-gray-300 dark:border-gray-600"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Project
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contractor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    {projects.length === 0
                      ? 'No construction projects yet. Create one to manage retentions and escrow.'
                      : `No projects as ${roleFilter === 'client' ? 'client' : 'contractor'}.`}
                  </td>
                </tr>
              ) : (
                filteredProjects.map((project) => (
                  <tr
                    key={project._id}
                    onClick={() => goToProject(project._id)}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selectedIds.has(project._id) ? 'bg-accent-50/50 dark:bg-accent-900/10' : ''}`}
                  >
                    <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(project._id)}
                        onChange={() => toggleSelect(project._id)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Briefcase className="w-5 h-5 text-gray-400 mr-2" />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 dark:text-white">
                              {project.project_ref}
                            </span>
                            {project.my_role && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                project.my_role === 'client' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                project.my_role === 'contractor' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                                'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                              }`}>
                                {project.my_role}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {project.project_name}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 ml-2 text-gray-400" />
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {((project.client_contact_ids as any)?.length
                        ? (project.client_contact_ids as any).map((c: any) => c?.name).filter(Boolean).join(', ')
                        : (project.client_contact_id as any)?.name) ?? (project.client_org_id as any)?.companyName ?? '—'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {(project.main_contractor_org_id as any)?.companyName ?? '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {project.country_code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatMoney(project.contract_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/projects/${project._id}/client-view`);
                          }}
                          className="inline-flex items-center px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          title="View as client"
                        >
                          <User className="w-4 h-4 mr-1" />
                          Client
                        </button>
                        <button
                          onClick={() => downloadReport(project._id, project.project_ref)}
                          className="inline-flex items-center px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                          title="Report"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {!isProjectAccepted(project) ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditProjectId(project._id);
                            }}
                            className="p-2 text-gray-500 hover:text-accent-600 dark:hover:text-accent-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                            title="Edit"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        ) : (
                          <span
                            className="p-2 text-gray-400 cursor-default"
                            title="Project accepted by client – use variations to make changes"
                          >
                            <Pencil className="w-4 h-4" />
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmId(project._id);
                          }}
                          className="p-2 rounded text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        ) : (
          <div className="p-6">
            {projects.length > 0 && (
              <div className="mb-4 flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filteredProjects.length > 0 && selectedIds.size === filteredProjects.length}
                  onChange={toggleSelectAll}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <span className="text-sm text-gray-500 dark:text-gray-400">Select all</span>
              </div>
            )}
            {filteredProjects.length === 0 ? (
              <div className="py-12 text-center text-gray-500 dark:text-gray-400">
                {projects.length === 0
                  ? 'No construction projects yet. Create one to manage retentions and escrow.'
                  : `No projects as ${roleFilter === 'client' ? 'client' : 'contractor'}.`}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {filteredProjects.map((project) => (
                  <div
                    key={project._id}
                    onClick={() => goToProject(project._id)}
                    className={`relative rounded-lg border dark:border-gray-600 p-4 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      selectedIds.has(project._id)
                        ? 'border-accent-500 bg-accent-50/50 dark:bg-accent-900/10 ring-2 ring-accent-500'
                        : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                    }`}
                  >
                    <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(project._id)}
                        onChange={() => toggleSelect(project._id)}
                        className="rounded border-gray-300 dark:border-gray-600"
                      />
                    </div>
                    <div className="flex items-start gap-3 pl-7">
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-accent-100 dark:bg-accent-900/30 flex items-center justify-center">
                        <Briefcase className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900 dark:text-white truncate">
                            {project.project_ref}
                          </span>
                          {project.my_role && (
                            <span className={`flex-shrink-0 text-xs px-1.5 py-0.5 rounded ${
                              project.my_role === 'client' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                              project.my_role === 'contractor' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                              'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                              {project.my_role}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                          {project.project_name}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
                          <span>Client: {((project.client_contact_ids as any)?.length
                            ? (project.client_contact_ids as any).map((c: any) => c?.name).filter(Boolean).join(', ')
                            : (project.client_contact_id as any)?.name) ?? (project.client_org_id as any)?.companyName ?? '—'}</span>
                          <span>·</span>
                          <span>Contractor: {(project.main_contractor_org_id as any)?.companyName ?? '—'}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span>{project.country_code}</span>
                          <span>·</span>
                          <span>{formatMoney(project.contract_value)}</span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    </div>
                    <div
                      className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/projects/${project._id}/client-view`);
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                        title="View as client"
                      >
                        <User className="w-3.5 h-3.5" />
                        Client
                      </button>
                      <button
                        onClick={() => downloadReport(project._id, project.project_ref)}
                        className="p-2 text-gray-500 hover:text-accent-600 dark:hover:text-accent-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                        title="Report"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      {!isProjectAccepted(project) ? (
                        <button
                          onClick={() => setEditProjectId(project._id)}
                          className="p-2 text-gray-500 hover:text-accent-600 dark:hover:text-accent-400 rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                      ) : (
                        <span
                          className="p-2 text-gray-400 cursor-default"
                          title="Project accepted by client – use variations to make changes"
                        >
                          <Pencil className="w-4 h-4" />
                        </span>
                      )}
                      <button
                        onClick={() => setDeleteConfirmId(project._id)}
                        className="p-2 text-gray-500 hover:text-red-600 dark:hover:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateProject && (
        <CreateProjectModal
          accounts={accounts}
          contacts={contacts}
          accountCountries={accountCountries}
          currentAccountId={user?.currentAccountId}
          onClose={() => setShowCreateProject(false)}
          onSuccess={(id) => {
            setShowCreateProject(false);
            fetchProjects();
            if (id) goToProject(id);
          }}
        />
      )}

      {editProjectId && (
        <EditProjectModal
          projectId={editProjectId}
          accounts={accounts}
          contacts={contacts}
          accountCountries={accountCountries}
          onClose={() => {
            setEditProjectId(null);
            setSelectedIds(new Set());
          }}
          onSuccess={() => {
            setEditProjectId(null);
            setSelectedIds(new Set());
            fetchProjects();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteConfirmId}
        title="Delete project"
        message={
          deleteConfirmId
            ? `Are you sure you want to delete "${projects.find((p) => p._id === deleteConfirmId)?.project_name || 'this project'}"? This cannot be undone.`
            : ''
        }
        type="danger"
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleteConfirmId) {
            deleteProject(deleteConfirmId);
          }
        }}
        onCancel={() => setDeleteConfirmId(null)}
      />

      <ConfirmDialog
        isOpen={bulkDeleteConfirmOpen}
        title="Delete projects"
        message={`Are you sure you want to delete ${selectedIds.size} project(s)? This cannot be undone.`}
        type="danger"
        confirmLabel="Delete"
        onConfirm={executeBulkDelete}
        onCancel={() => setBulkDeleteConfirmOpen(false)}
      />
    </div>
  );
};

/** Format amount as $0,000,000 */
const formatMoney = (n: number | undefined | null) =>
  n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '-';
const parseMoney = (s: string) => parseFloat(s.replace(/[,$]/g, '')) || 0;

interface MilestoneDraft {
  id: string;
  title: string;
  amount: number;
  due_date: string;
  qty?: number;
  unit_price?: number;
}

interface ClaimedItemDraft {
  id: string;
  title: string;
  percent: number;
  amount?: number;
  qty?: number;
  unit_price?: number;
}

interface RetentionTierDraft {
  id: string;
  min_value: number;
  max_value: number | null; // null = "and above"
  percent: number;
}

function EditProjectModal({
  projectId,
  accounts,
  contacts,
  accountCountries,
  onClose,
  onSuccess,
}: {
  projectId: string;
  accounts: Account[];
  contacts: Contact[];
  accountCountries: { code: string; name: string }[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<{
    project_name: string;
    description: string;
    country_code: string;
    contract_value: number;
    start_date: string;
    forecast_practical_completion: string;
    defects_liability_months: number;
    retention_default_percent: number;
    retention_cap_percent: number;
    client_contact_ids: string[];
    main_contractor_org_id: string;
    default_currency: string;
    schedule_type: 'milestone' | 'claimed_percent';
    funding_structure: { escrow_mobilisation: boolean; escrow_milestones: boolean; retention_enabled: boolean };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (data.success && data.data) {
          const p = data.data;
          setStep(1);
          setForm({
            project_name: p.project_name || '',
            description: p.description || '',
            country_code: p.country_code || 'NZ',
            contract_value: p.contract_value ?? 0,
            start_date: p.start_date ? new Date(p.start_date).toISOString().slice(0, 10) : '',
            forecast_practical_completion: p.forecast_practical_completion
              ? new Date(p.forecast_practical_completion).toISOString().slice(0, 10)
              : '',
            defects_liability_months: p.defects_liability_months ?? 12,
            retention_default_percent: p.retention_default_percent ?? 5,
            retention_cap_percent: p.retention_cap_percent ?? 5,
            client_contact_ids: (p.client_contact_ids as any)?.map((c: any) => c?._id ?? c) ?? (p.client_contact_id ? [p.client_contact_id?._id ?? p.client_contact_id] : []),
            main_contractor_org_id: p.main_contractor_org_id?._id ?? p.main_contractor_org_id ?? '',
            default_currency: p.default_currency || 'NZD',
            schedule_type: p.schedule_type || 'claimed_percent',
            funding_structure: p.funding_structure
              ? { ...p.funding_structure }
              : { escrow_mobilisation: false, escrow_milestones: true, retention_enabled: true },
          });
        }
      } catch {
        setForm(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiUrl, projectId, token]);

  useEffect(() => {
    if (form && accountCountries.length > 0) {
      setForm((f) =>
        f && accountCountries.some((c) => c.code === f.country_code)
          ? f
          : f
            ? { ...f, country_code: accountCountries[0]?.code ?? f.country_code }
            : f
      );
    }
  }, [accountCountries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form || !token) return;
    setSubmitting(true);
    try {
      const payload = {
        project_name: form.project_name.trim(),
        description: form.description.trim(),
        country_code: form.country_code,
        start_date: form.start_date,
        forecast_practical_completion: form.forecast_practical_completion || form.start_date,
        defects_liability_months: form.defects_liability_months,
        retention_default_percent: form.retention_default_percent,
        retention_cap_percent: form.retention_cap_percent,
        client_contact_ids: form.client_contact_ids?.length ? form.client_contact_ids : undefined,
        default_currency: form.default_currency,
        schedule_type: form.schedule_type,
        funding_structure: form.funding_structure,
      };
      const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) onSuccess();
      else {
        if (res.status === 403) {
          onClose();
        }
        alert(data.message || 'Failed to update project');
      }
    } catch {
      alert('Failed to update project');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <Loader className="w-8 h-8 animate-spin text-accent-600" />
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6">
          <p className="text-gray-600 dark:text-gray-400">Project not found</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            Close
          </button>
        </div>
      </div>
    );
  }

  const countryFlag = (code: string) =>
    code.toUpperCase().split('').map((c) => String.fromCodePoint(127397 + c.charCodeAt(0))).join('');

  const maxStep = 2;
  const stepLabels = ['Project details & client', 'Retention & dates'];
  const canProceedStep1 = form.project_name.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[95vw] max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Edit Project</h3>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
              ×
            </button>
          </div>

          <div className="flex items-center gap-2 mb-6">
            {[1, 2].map((s) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                      step === s
                        ? 'bg-accent-600 text-white'
                        : step > s
                          ? 'bg-accent-100 dark:bg-accent-900/40 text-accent-600 dark:text-accent-400'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {s}
                  </div>
                  <span
                    className={`text-sm hidden sm:inline ${
                      step === s ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {stepLabels[s - 1]}
                  </span>
                </div>
                {s < maxStep && (
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600 min-w-[20px]" />
                )}
              </React.Fragment>
            ))}
          </div>

          {step === 1 && (
            <form
              id="edit-project-step1-form"
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (canProceedStep1) setStep(2);
              }}
            >
            <div>
              <label className="block text-sm font-medium mb-1">Project Name *</label>
              <input
                required
                value={form.project_name}
                onChange={(e) => setForm((f) => f ? { ...f, project_name: e.target.value } : f)}
                placeholder="e.g. Main Street Development"
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            <DescriptionBox
              label="Project Description"
              value={form.description}
              onChange={(v) => setForm((f) => f ? { ...f, description: v } : f)}
              placeholder="Describe the project scope, deliverables, and key details. Use voice input or AI cleanup."
              rows={4}
              aiContextHint="project description"
            />
            <div>
              <label className="block text-sm font-medium mb-1">Schedule Type</label>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setForm((f) => f ? { ...f, schedule_type: 'milestone' } : f)}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    form.schedule_type === 'milestone'
                      ? 'bg-accent-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Milestones
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => f ? { ...f, schedule_type: 'claimed_percent' } : f)}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    form.schedule_type === 'claimed_percent'
                      ? 'bg-accent-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Claimed %
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {form.schedule_type === 'milestone'
                  ? 'Fixed amounts – claim 100% per milestone when completed'
                  : 'Percent of total – retentions are built on claimed %'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Retentions</label>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setForm((f) => f ? { ...f, funding_structure: { ...f.funding_structure, retention_enabled: true } } : f)}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    form.funding_structure.retention_enabled
                      ? 'bg-accent-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => f ? { ...f, funding_structure: { ...f.funding_structure, retention_enabled: false } } : f)}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    !form.funding_structure.retention_enabled
                      ? 'bg-accent-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  No
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Retentions are retained by the client and paid out from the escrow on acceptance by both parties.
              </p>
            </div>
            {accountCountries.length !== 1 && (
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <div className="flex flex-wrap gap-2">
                  {(accountCountries.length > 0 ? accountCountries : [
                    { code: 'NZ', name: 'New Zealand' },
                    { code: 'AU', name: 'Australia' },
                  ]).map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      onClick={() => setForm((f) => f ? { ...f, country_code: c.code } : f)}
                      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        form.country_code === c.code
                          ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300 ring-2 ring-accent-500'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                      }`}
                    >
                      <span className="text-xl">{countryFlag(c.code)}</span>
                      <span>{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Clients (select all that apply)</label>
              {contacts.length > 0 ? (
                <ContactAutocompleteMultiSelect
                  contacts={contacts}
                  selected={form.client_contact_ids}
                  onChange={(ids) => setForm((f) => (f ? { ...f, client_contact_ids: ids } : f))}
                  placeholder="Type to search contacts..."
                />
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                  No clients yet. Add clients in Contacts first.
                </p>
              )}
            </div>
            </form>
          )}

          {step === 2 && (
            <form id="edit-project-step2-form" onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  required
                  value={form.start_date}
                  onChange={(e) => setForm((f) => f ? { ...f, start_date: e.target.value } : f)}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Forecast Completion</label>
                <input
                  type="date"
                  value={form.forecast_practical_completion}
                  onChange={(e) => setForm((f) => f ? { ...f, forecast_practical_completion: e.target.value } : f)}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Retention %</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={form.retention_default_percent}
                  onChange={(e) => setForm((f) => f ? { ...f, retention_default_percent: parseFloat(e.target.value) || 0 } : f)}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Retention Cap %</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={form.retention_cap_percent}
                  onChange={(e) => setForm((f) => f ? { ...f, retention_cap_percent: parseFloat(e.target.value) || 0 } : f)}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Defects Liability (months)</label>
              <input
                type="number"
                min="0"
                value={form.defects_liability_months}
                onChange={(e) => setForm((f) => f ? { ...f, defects_liability_months: parseInt(e.target.value) || 0 } : f)}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            </form>
          )}

          <div className="flex justify-between pt-6 mt-4 border-t border-gray-200 dark:border-gray-600">
            <div>
              {step === 1 ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
            </div>
            <div>
              {step === 1 ? (
                <button
                  type="submit"
                  form="edit-project-step1-form"
                  disabled={!canProceedStep1}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  form="edit-project-step2-form"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateProjectModal({
  accounts,
  contacts,
  accountCountries,
  currentAccountId,
  onClose,
  onSuccess,
}: {
  accounts: Account[];
  contacts: Contact[];
  accountCountries: { code: string; name: string }[];
  currentAccountId?: string;
  onClose: () => void;
  onSuccess: (projectId?: string) => void;
}) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    project_name: '',
    description: '',
    country_code: 'NZ',
    schedule_type: 'claimed_percent' as 'milestone' | 'claimed_percent',
    contract_value: 0,
    start_date: new Date().toISOString().slice(0, 10),
    forecast_practical_completion: '',
    defects_liability_months: 12,
    retention_default_percent: 5,
    retention_cap_percent: 5,
    client_contact_ids: [] as string[],
    main_contractor_org_id: '',
    default_currency: 'NZD',
    funding_structure: {
      escrow_mobilisation: false,
      escrow_milestones: true,
      retention_enabled: true,
    },
  });
  const [milestones, setMilestones] = useState<MilestoneDraft[]>([]);
  const [claimedItems, setClaimedItems] = useState<ClaimedItemDraft[]>([]);
  const [showPromptPopup, setShowPromptPopup] = useState(false);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingRawText, setPendingRawText] = useState<string | null>(null);
  const [promptPopupValue, setPromptPopupValue] = useState('');
  const [isRetryPopup, setIsRetryPopup] = useState(false);
  const [lastParsedFile, setLastParsedFile] = useState<File | null>(null);
  const [lastParsedRawText, setLastParsedRawText] = useState<string | null>(null);
  const [lastParsedCustomPrompt, setLastParsedCustomPrompt] = useState('');
  const [retentionTiers, setRetentionTiers] = useState<RetentionTierDraft[]>([
    { id: crypto.randomUUID(), min_value: 0, max_value: 100000, percent: 10 },
    { id: crypto.randomUUID(), min_value: 100000, max_value: 500000, percent: 5 },
    { id: crypto.randomUUID(), min_value: 500000, max_value: 1000000, percent: 2 },
    { id: crypto.randomUUID(), min_value: 1000000, max_value: null, percent: 1 },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [parsingExcel, setParsingExcel] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const countryFlag = (code: string) =>
    code.toUpperCase().split('').map((c) => String.fromCodePoint(127397 + c.charCodeAt(0))).join('');

  useEffect(() => {
    if (accountCountries.length > 0) {
      setForm((f) =>
        accountCountries.some((c) => c.code === f.country_code)
          ? f
          : { ...f, country_code: accountCountries[0].code }
      );
    }
  }, [accountCountries]);

  const addMilestone = () => {
    setMilestones((m) => {
      const n = m.length + 1;
      return [...m, { id: crypto.randomUUID(), title: `Milestone ${n}`, amount: 0, due_date: '' }];
    });
  };

  const updateMilestone = (id: string, field: keyof MilestoneDraft, value: string | number) => {
    setMilestones((m) =>
      m.map((x) => {
        if (x.id !== id) return x;
        const next = { ...x, [field]: value };
        if (field === 'qty' || field === 'unit_price') {
          const qty = field === 'qty' ? (value as number) : (next.qty ?? 0);
          const unit_price = field === 'unit_price' ? (value as number) : (next.unit_price ?? 0);
          if (qty > 0 && unit_price > 0) next.amount = qty * unit_price;
        }
        return next;
      })
    );
  };

  const removeMilestone = (id: string) => {
    setMilestones((m) => m.filter((x) => x.id !== id));
  };

  const addClaimedItem = () => {
    setClaimedItems((c) => {
      const n = c.length + 1;
      return [...c, { id: crypto.randomUUID(), title: `Item ${n}`, percent: 0, amount: undefined, qty: undefined, unit_price: undefined }];
    });
  };

  const updateClaimedItem = (id: string, field: keyof ClaimedItemDraft, value: string | number) => {
    setClaimedItems((c) =>
      c.map((x) => {
        if (x.id !== id) return x;
        const next = { ...x, [field]: value };
        if (field === 'qty' || field === 'unit_price' || field === 'amount') {
          const qty = field === 'qty' ? (value as number) : (next.qty ?? 0);
          const unit_price = field === 'unit_price' ? (value as number) : (next.unit_price ?? 0);
          const amount = qty > 0 && unit_price > 0 ? qty * unit_price : (field === 'amount' ? (value as number) : next.amount ?? 0);
          if (amount > 0) {
            next.amount = amount;
            if (form.contract_value > 0) next.percent = (amount / form.contract_value) * 100;
          }
        }
        return next;
      })
    );
  };

  const removeClaimedItem = (id: string) => {
    setClaimedItems((c) => c.filter((x) => x.id !== id));
  };

  const addRetentionTier = () => {
    const last = retentionTiers[retentionTiers.length - 1];
    if (!last) {
      setRetentionTiers([{ id: crypto.randomUUID(), min_value: 0, max_value: 100000, percent: 10 }]);
      return;
    }
    const nextMin = last.max_value != null ? last.max_value : last.min_value + 400000;
    const newTier: RetentionTierDraft = {
      id: crypto.randomUUID(),
      min_value: nextMin,
      max_value: null,
      percent: last.percent,
    };
    if (last.max_value == null) {
      setRetentionTiers([
        ...retentionTiers.slice(0, -1),
        { ...last, max_value: nextMin },
        newTier,
      ]);
    } else {
      setRetentionTiers([...retentionTiers, newTier]);
    }
  };

  const updateRetentionTier = (id: string, field: keyof RetentionTierDraft, value: number | null) => {
    setRetentionTiers((t) =>
      t.map((x) => (x.id === id ? { ...x, [field]: value } : x))
    );
  };

  const removeRetentionTier = (id: string) => {
    setRetentionTiers((t) => t.filter((x) => x.id !== id));
  };

  const excelInputRef = useRef<HTMLInputElement>(null);
  const [dropZoneActive, setDropZoneActive] = useState(false);

  const applyParsedItems = (items: { title: string; amount?: number; due_date?: string; percent?: number; qty?: number; unit_price?: number }[]) => {
    if (form.schedule_type === 'milestone') {
      setMilestones(
        items.map((m, i) => ({
          id: crypto.randomUUID(),
          title: m.title || `Milestone ${i + 1}`,
          amount: m.amount ?? (m.qty != null && m.unit_price != null ? m.qty * m.unit_price : 0),
          due_date: m.due_date || '',
          qty: m.qty,
          unit_price: m.unit_price,
        }))
      );
    } else {
      setClaimedItems(
        items.map((c, i) => {
          const amount = c.amount ?? (c.qty != null && c.unit_price != null ? c.qty * c.unit_price : 0);
          const percent = c.percent ?? (amount > 0 && form.contract_value > 0 ? (amount / form.contract_value) * 100 : 0);
          return {
            id: crypto.randomUUID(),
            title: c.title || `Item ${i + 1}`,
            percent,
            amount: amount > 0 ? amount : undefined,
            qty: c.qty,
            unit_price: c.unit_price,
          };
        })
      );
    }
  };

  const doParseFromFile = async (file: File, customPrompt: string, fromRetry = false) => {
    if (!token) return;
    setParsingExcel(true);
    if (!fromRetry) {
      setShowPromptPopup(false);
      setPendingFile(null);
      setPendingRawText(null);
      setPromptPopupValue('');
    }
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append(
        'intent',
        form.schedule_type === 'milestone' ? 'update_milestones' : 'upload_items'
      );
      if (customPrompt.trim()) fd.append('customPrompt', customPrompt.trim());
      const res = await fetch(`${apiUrl}/api/construction/parse-milestones-from-excel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'Failed to parse file');
        return;
      }
      applyParsedItems(data.data || []);
      setLastParsedFile(file);
      setLastParsedRawText(null);
      setLastParsedCustomPrompt(customPrompt);
    } catch {
      alert('Failed to parse file');
    } finally {
      setParsingExcel(false);
      if (fromRetry) {
        setShowPromptPopup(false);
        setPendingFile(null);
        setPendingRawText(null);
        setPromptPopupValue('');
        setIsRetryPopup(false);
      }
    }
  };

  const doParseFromText = async (rawText: string, customPrompt: string, fromRetry = false) => {
    if (!token || !rawText.trim()) return;
    setParsingExcel(true);
    if (!fromRetry) {
      setShowPromptPopup(false);
      setPendingFile(null);
      setPendingRawText(null);
      setPromptPopupValue('');
    }
    try {
      const res = await fetch(`${apiUrl}/api/construction/parse-milestones-from-text`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          intent: form.schedule_type === 'milestone' ? 'update_milestones' : 'upload_items',
          rawText: rawText.trim(),
          ...(customPrompt.trim() && { customPrompt: customPrompt.trim() }),
        }),
      });
      let data: { success?: boolean; message?: string; data?: { title: string; amount?: number; due_date?: string; percent?: number }[] };
      try {
        data = await res.json();
      } catch {
        alert('Invalid response from server. Please try again.');
        return;
      }
      if (!data.success) {
        alert(data.message || 'Could not parse. Use tab or comma-separated columns (e.g. Title, Amount, Date).');
        return;
      }
      applyParsedItems(data.data || []);
      setLastParsedFile(null);
      setLastParsedRawText(rawText);
      setLastParsedCustomPrompt(customPrompt);
    } catch (err) {
      const isNetwork = err instanceof TypeError && err.message === 'Failed to fetch';
      alert(isNetwork ? 'Connection error. Check that the backend is running.' : 'Could not parse pasted data. Try tab or comma-separated format.');
    } finally {
      setParsingExcel(false);
      if (fromRetry) {
        setShowPromptPopup(false);
        setPendingFile(null);
        setPendingRawText(null);
        setPromptPopupValue('');
        setIsRetryPopup(false);
      }
    }
  };

  const handleRetryWithPrompt = () => {
    if (lastParsedFile) {
      setPendingFile(lastParsedFile);
      setPendingRawText(null);
    } else if (lastParsedRawText) {
      setPendingRawText(lastParsedRawText);
      setPendingFile(null);
    } else return;
    setPromptPopupValue('');
    setIsRetryPopup(true);
    setShowPromptPopup(true);
  };

  const handleConfirmPromptPopup = () => {
    const customPrompt = isRetryPopup
      ? [lastParsedCustomPrompt, promptPopupValue].filter(Boolean).join('\n\nCorrection / additional instructions: ')
      : promptPopupValue;
    if (pendingFile) {
      doParseFromFile(pendingFile, customPrompt, isRetryPopup);
    } else if (pendingRawText) {
      doParseFromText(pendingRawText, customPrompt, isRetryPopup);
    }
  };

  const handleCancelPromptPopup = () => {
    setShowPromptPopup(false);
    setPendingFile(null);
    setPendingRawText(null);
    setPromptPopupValue('');
    setIsRetryPopup(false);
  };

  const handleParseExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file && isExcelOrCsv(file)) {
      setPendingFile(file);
      setPendingRawText(null);
      setPromptPopupValue('');
      setIsRetryPopup(false);
      setShowPromptPopup(true);
    }
  };

  const isExcelOrCsv = (f: File) => {
    const n = f.name.toLowerCase();
    return n.endsWith('.xlsx') || n.endsWith('.xls') || n.endsWith('.csv');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDropZoneActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && isExcelOrCsv(file)) {
      setPendingFile(file);
      setPendingRawText(null);
      setPromptPopupValue('');
      setIsRetryPopup(false);
      setShowPromptPopup(true);
    } else if (file) alert('Please drop an Excel (.xlsx, .xls) or CSV file');
  };

  const handlePaste = async (e?: React.ClipboardEvent) => {
    const text = e?.clipboardData?.getData('text/plain');
    let raw = text?.trim();
    if (!raw) {
      try {
        raw = (await navigator.clipboard.readText())?.trim() || undefined;
      } catch {
        /* clipboard not available */
      }
    }
    if (raw) {
      e?.preventDefault();
      setPendingRawText(raw);
      setPendingFile(null);
      setPromptPopupValue('');
      setIsRetryPopup(false);
      setShowPromptPopup(true);
    }
  };

  const canProceedStep1 = form.project_name.trim().length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.project_name.trim()) return;
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        project_name: form.project_name.trim(),
        ...(form.description.trim() && { description: form.description.trim() }),
        country_code: form.country_code,
        schedule_type: form.schedule_type,
        contract_value: form.contract_value,
        start_date: form.start_date,
        forecast_practical_completion: form.forecast_practical_completion || form.start_date,
        defects_liability_months: form.defects_liability_months,
        retention_default_percent: form.retention_default_percent,
        retention_cap_percent: form.retention_cap_percent,
        default_currency: form.default_currency,
        funding_structure: form.funding_structure,
      };
      if (form.funding_structure.retention_enabled && retentionTiers.length > 0) {
        payload.retention_tiers = retentionTiers.map((t) => ({
          min_value: t.min_value,
          ...(t.max_value != null && { max_value: t.max_value }),
          percent: t.percent,
        }));
      }
      if (form.client_contact_ids?.length) payload.client_contact_ids = form.client_contact_ids;
      if (form.main_contractor_org_id) payload.main_contractor_org_id = form.main_contractor_org_id;

      const res = await fetch(`${apiUrl}/api/construction/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'Failed to create project');
        return;
      }

      const projectId = data.data?._id;
      const accountId = currentAccountId || accounts[0]?._id;

      if (projectId && accountId) {
        if (form.schedule_type === 'milestone' && milestones.length > 0) {
          const validMilestones = milestones.filter((m) => m.title.trim() && m.amount > 0);
          if (validMilestones.length > 0) {
            const totalAmount = validMilestones.reduce((s, m) => s + m.amount, 0);
            const agreementRes = await fetch(`${apiUrl}/api/construction/escrow-agreements`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                project_id: projectId,
                schedule_type: 'milestone',
                type: 'milestone',
                payer_org_id: accountId,
                payee_org_id: form.main_contractor_org_id || accountId,
                total_amount: totalAmount,
                currency: form.default_currency,
                country_code: form.country_code,
              }),
            });
            const agreementData = await agreementRes.json();
            if (agreementData.success && agreementData.data?._id) {
              for (const m of validMilestones) {
                await fetch(`${apiUrl}/api/construction/escrow-agreements/${agreementData.data._id}/milestones`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    title: m.title.trim(),
                    amount: m.amount,
                    due_date: m.due_date || undefined,
                    evidence_required: false,
                    evidence_types: [],
                  }),
                });
              }
            }
          }
        } else if (form.schedule_type === 'claimed_percent' && claimedItems.length > 0) {
          const validItems = claimedItems.filter((c) => c.title.trim() && c.percent > 0);
          if (validItems.length > 0) {
            const totalAmount = form.contract_value || 0;
            const agreementRes = await fetch(`${apiUrl}/api/construction/escrow-agreements`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                project_id: projectId,
                schedule_type: 'claimed_percent',
                type: 'claimed_percent',
                payer_org_id: accountId,
                payee_org_id: form.main_contractor_org_id || accountId,
                total_amount: totalAmount,
                currency: form.default_currency,
                country_code: form.country_code,
              }),
            });
            const agreementData = await agreementRes.json();
            if (agreementData.success && agreementData.data?._id) {
              for (const m of validItems) {
                await fetch(`${apiUrl}/api/construction/escrow-agreements/${agreementData.data._id}/milestones`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({
                    title: m.title.trim(),
                    percent: m.percent,
                    amount: Math.round((totalAmount * (m.percent / 100)) * 100) / 100,
                    evidence_required: false,
                    evidence_types: [],
                  }),
                });
              }
            }
          }
        }
      }

      onSuccess(projectId);
    } catch {
      alert('Failed to create project');
    } finally {
      setSubmitting(false);
    }
  };

  const retentionEnabled = form.funding_structure.retention_enabled;
  const maxStep = retentionEnabled ? 3 : 2;
  const stepLabels = [
    'Type, country, name, description & client',
    form.schedule_type === 'milestone' ? 'Milestones' : 'Claim items',
    'Retention settings',
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[95vw] max-w-5xl max-h-[95vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Create Project</h3>

          <div className="flex items-center gap-2 mb-6">
            {[1, 2, ...(retentionEnabled ? [3] : [])].map((s) => (
              <React.Fragment key={s}>
                <div className="flex items-center gap-2">
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold ${
                      step === s
                        ? 'bg-accent-600 text-white'
                        : step > s
                          ? 'bg-accent-100 dark:bg-accent-900/40 text-accent-600 dark:text-accent-400'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {s}
                  </div>
                  <span
                    className={`text-sm hidden sm:inline ${
                      step === s ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {stepLabels[s - 1]}
                  </span>
                </div>
                {s < maxStep && (
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-600 min-w-[20px]" />
                )}
              </React.Fragment>
            ))}
          </div>

          {step === 1 && (
            <form
              id="create-project-step1-form"
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (canProceedStep1) setStep(2);
              }}
            >
              <div>
                <label className="block text-sm font-medium mb-1">Schedule Type</label>
                <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, schedule_type: 'milestone' }))}
                    className={`flex-1 px-4 py-2 text-sm font-medium ${
                      form.schedule_type === 'milestone'
                        ? 'bg-accent-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Milestones
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, schedule_type: 'claimed_percent' }))}
                    className={`flex-1 px-4 py-2 text-sm font-medium ${
                      form.schedule_type === 'claimed_percent'
                        ? 'bg-accent-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Claimed %
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {form.schedule_type === 'milestone'
                    ? 'Fixed amounts – claim 100% per milestone when completed'
                    : 'Percent of total – retentions are built on claimed %'}
                </p>
              </div>

              {accountCountries.length !== 1 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Country</label>
                  <div className="flex flex-wrap gap-2">
                    {(accountCountries.length > 0 ? accountCountries : [
                      { code: 'NZ', name: 'New Zealand' },
                      { code: 'AU', name: 'Australia' },
                    ]).map((c) => (
                      <button
                        key={c.code}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, country_code: c.code }))}
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                          form.country_code === c.code
                            ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300 ring-2 ring-accent-500'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}
                      >
                        <span className="text-xl">{countryFlag(c.code)}</span>
                        <span>{c.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Project Name *</label>
                <input
                  required
                  value={form.project_name}
                  onChange={(e) => setForm((f) => ({ ...f, project_name: e.target.value }))}
                  placeholder="e.g. Main Street Development"
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <DescriptionBox
                label="Project Description"
                value={form.description}
                onChange={(v) => setForm((f) => ({ ...f, description: v }))}
                placeholder="Describe the project scope, deliverables, and key details. Use voice input or AI cleanup."
                rows={4}
                aiContextHint="project description"
              />

              <div>
                <label className="block text-sm font-medium mb-1">Retentions</label>
                <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, funding_structure: { ...f.funding_structure, retention_enabled: true } }))}
                    className={`flex-1 px-4 py-2 text-sm font-medium ${
                      form.funding_structure.retention_enabled
                        ? 'bg-accent-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, funding_structure: { ...f.funding_structure, retention_enabled: false } }))}
                    className={`flex-1 px-4 py-2 text-sm font-medium ${
                      !form.funding_structure.retention_enabled
                        ? 'bg-accent-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    No
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Retentions are retained by the client and paid out from the escrow on acceptance by both parties.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Clients (select all that apply)</label>
                {contacts.length > 0 ? (
                  <ContactAutocompleteMultiSelect
                    contacts={contacts}
                    selected={form.client_contact_ids}
                    onChange={(ids) => setForm((f) => ({ ...f, client_contact_ids: ids }))}
                    placeholder="Type to search contacts..."
                  />
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                    No clients yet. Add clients in Contacts first.
                  </p>
                )}
              </div>
            </form>
          )}

          {step === 2 && (
            <form
              id="create-project-step2-form"
              onSubmit={(e) => {
                e.preventDefault();
                if (retentionEnabled) setStep(3);
                else handleSubmit(e);
              }}
              className="space-y-4"
            >
              <input
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleParseExcel}
                className="hidden"
              />
              {form.schedule_type === 'milestone' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Payment Milestones</label>
                    <button
                      type="button"
                      onClick={addMilestone}
                      className="text-sm text-accent-600 hover:text-accent-700"
                    >
                      + Add Milestone
                    </button>
                  </div>
                  <div
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => e.key === 'Enter' && excelInputRef.current?.click()}
                    onClick={() => excelInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropZoneActive(true); }}
                    onDragLeave={() => setDropZoneActive(false)}
                    onDrop={handleDrop}
                    onPaste={(e) => handlePaste(e)}
                    className={`mb-2 rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
                      dropZoneActive
                        ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-accent-400 dark:hover:border-accent-500'
                    } ${parsingExcel ? 'pointer-events-none opacity-70' : ''}`}
                  >
                    {parsingExcel ? (
                      <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Loader className="w-4 h-4 animate-spin" />
                        Parsing...
                      </span>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-gray-500 dark:text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Drop Excel or CSV here, or <span className="text-accent-600 font-medium">click to browse</span>, or{' '}
                          <button type="button" onClick={(ev) => { ev.stopPropagation(); handlePaste(); }} className="text-accent-600 font-medium hover:underline">
                            paste
                          </button>{' '}
                          (Ctrl+V)
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          AI will extract titles, qty, unit price, total, dates. Data can be messy.
                        </p>
                      </>
                    )}
                  </div>
                  {milestones.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                      No milestones yet. Add manually or upload Excel.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {(lastParsedFile || lastParsedRawText) && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleRetryWithPrompt}
                            disabled={parsingExcel}
                            className="text-sm text-accent-600 hover:text-accent-700 disabled:opacity-50"
                          >
                            Retry with prompt
                          </button>
                        </div>
                      )}
                      {milestones.map((m, idx) => (
                        <div
                          key={m.id}
                          className="flex gap-2 items-start p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <span className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400 w-24 pt-2">
                            Milestone {idx + 1}
                          </span>
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Name</label>
                              <input
                                value={m.title}
                                onChange={(e) => updateMilestone(m.id, 'title', e.target.value)}
                                placeholder="Milestone name"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Qty</label>
                              <input
                                type="text"
                                value={m.qty != null && m.qty !== 0 ? String(m.qty) : ''}
                                onChange={(e) => updateMilestone(m.id, 'qty', parseFloat(e.target.value) || 0)}
                                placeholder="-"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Unit price</label>
                              <input
                                type="text"
                                value={m.unit_price != null ? formatMoney(m.unit_price) : ''}
                                onChange={(e) => updateMilestone(m.id, 'unit_price', parseMoney(e.target.value))}
                                placeholder="-"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total</label>
                              <input
                                type="text"
                                value={m.amount ? formatMoney(m.amount) : ''}
                                onChange={(e) => updateMilestone(m.id, 'amount', parseMoney(e.target.value))}
                                placeholder="$0"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Date</label>
                              <input
                                type="date"
                                value={m.due_date}
                                onChange={(e) => updateMilestone(m.id, 'due_date', e.target.value)}
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeMilestone(m.id)}
                            className="p-2 text-gray-500 hover:text-red-600"
                            aria-label="Remove milestone"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {form.schedule_type === 'claimed_percent' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Claim Items</label>
                    <button
                      type="button"
                      onClick={addClaimedItem}
                      className="text-sm text-accent-600 hover:text-accent-700"
                    >
                      + Add Item
                    </button>
                  </div>
                  <div
                    tabIndex={0}
                    role="button"
                    onKeyDown={(e) => e.key === 'Enter' && excelInputRef.current?.click()}
                    onClick={() => excelInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDropZoneActive(true); }}
                    onDragLeave={() => setDropZoneActive(false)}
                    onDrop={handleDrop}
                    onPaste={(e) => handlePaste(e)}
                    className={`mb-2 rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition-colors ${
                      dropZoneActive
                        ? 'border-accent-500 bg-accent-50 dark:bg-accent-900/20'
                        : 'border-gray-300 dark:border-gray-600 hover:border-accent-400 dark:hover:border-accent-500'
                    } ${parsingExcel ? 'pointer-events-none opacity-70' : ''}`}
                  >
                    {parsingExcel ? (
                      <span className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <Loader className="w-4 h-4 animate-spin" />
                        Parsing...
                      </span>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 text-gray-500 dark:text-gray-400" />
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Drop Excel or CSV here, or <span className="text-accent-600 font-medium">click to browse</span>, or{' '}
                          <button type="button" onClick={(ev) => { ev.stopPropagation(); handlePaste(); }} className="text-accent-600 font-medium hover:underline">
                            paste
                          </button>{' '}
                          (Ctrl+V)
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          AI will extract item names, qty, unit price, total, %. Data can be messy.
                        </p>
                      </>
                    )}
                  </div>
                  {claimedItems.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                      No items yet. Add manually or upload Excel.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {(lastParsedFile || lastParsedRawText) && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleRetryWithPrompt}
                            disabled={parsingExcel}
                            className="text-sm text-accent-600 hover:text-accent-700 disabled:opacity-50"
                          >
                            Retry with prompt
                          </button>
                        </div>
                      )}
                      {claimedItems.map((c, idx) => (
                        <div
                          key={c.id}
                          className="flex gap-2 items-start p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                        >
                          <span className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400 w-16 pt-2">
                            Item {idx + 1}
                          </span>
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Name</label>
                              <input
                                value={c.title}
                                onChange={(e) => updateClaimedItem(c.id, 'title', e.target.value)}
                                placeholder="Item name"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Qty</label>
                              <input
                                type="text"
                                value={c.qty != null && c.qty !== 0 ? String(c.qty) : ''}
                                onChange={(e) => updateClaimedItem(c.id, 'qty', parseFloat(e.target.value) || 0)}
                                placeholder="-"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Unit price</label>
                              <input
                                type="text"
                                value={c.unit_price != null && c.unit_price > 0 ? formatMoney(c.unit_price) : ''}
                                onChange={(e) => updateClaimedItem(c.id, 'unit_price', parseMoney(e.target.value))}
                                placeholder="-"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Total</label>
                              <input
                                type="text"
                                value={c.amount != null && c.amount > 0 ? formatMoney(c.amount) : ''}
                                onChange={(e) => updateClaimedItem(c.id, 'amount', parseMoney(e.target.value))}
                                placeholder="-"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">% of total</label>
                              <input
                                type="number"
                                step="0.1"
                                min="0"
                                max="100"
                                value={c.percent || ''}
                                onChange={(e) => updateClaimedItem(c.id, 'percent', parseFloat(e.target.value) || 0)}
                                placeholder="0"
                                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeClaimedItem(c.id)}
                            className="p-2 text-gray-500 hover:text-red-600"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {claimedItems.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Total: {claimedItems.reduce((s, c) => s + c.percent, 0).toFixed(1)}%
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </form>
          )}

          {step === 3 && retentionEnabled && (
            <form id="create-project-step3-form" onSubmit={handleSubmit} className="space-y-4">
              <div className="rounded-lg border border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-700/30">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Retentions are retained by the client and paid out from the escrow on acceptance by both parties.
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Define retention % by contract value range. The rate applied depends on which tier the contract value falls into.
                </p>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium">Retention tiers (contract value → %)</label>
                  <button
                    type="button"
                    onClick={addRetentionTier}
                    className="text-sm text-accent-600 hover:text-accent-700"
                  >
                    + Add tier
                  </button>
                </div>
                <div className="space-y-3">
                  {retentionTiers.map((t, idx) => (
                    <div
                      key={t.id}
                      className="flex gap-2 items-center p-3 bg-white dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      <span className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400 w-20">
                        Tier {idx + 1}
                      </span>
                      <div className="flex-1 grid grid-cols-1 sm:grid-cols-4 gap-2">
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">From ($)</label>
                          <input
                            type="text"
                            value={t.min_value ? formatMoney(t.min_value) : ''}
                            onChange={(e) => updateRetentionTier(t.id, 'min_value', parseMoney(e.target.value))}
                            placeholder="0"
                            className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">To ($)</label>
                          <input
                            type="text"
                            value={t.max_value != null ? formatMoney(t.max_value) : ''}
                            onChange={(e) => {
                              const v = e.target.value.trim();
                              updateRetentionTier(t.id, 'max_value', v ? parseMoney(v) : null);
                            }}
                            placeholder="+ (and above)"
                            className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">%</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            value={t.percent || ''}
                            onChange={(e) => updateRetentionTier(t.id, 'percent', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeRetentionTier(t.id)}
                        className="p-2 text-gray-500 hover:text-red-600 flex-shrink-0"
                        aria-label="Remove tier"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {retentionTiers.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                    No tiers yet. Add a tier to define retention by contract value range.
                  </p>
                )}
              </div>
            </form>
          )}

          {showPromptPopup && (pendingFile || pendingRawText) && (
            <div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[120] p-4"
              onClick={(e) => e.target === e.currentTarget && !parsingExcel && handleCancelPromptPopup()}
            >
              <div
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
              >
                {parsingExcel && isRetryPopup ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader className="w-10 h-10 animate-spin text-accent-600 mb-4" />
                    <p className="text-lg font-medium text-gray-900 dark:text-gray-100">Retrying...</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Re-parsing with your instructions</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {isRetryPopup ? 'Retry with additional instructions' : 'Add custom instructions (optional)'}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                      {isRetryPopup
                        ? 'Re-parse the same data with correction instructions. Add what to fix (e.g. amounts in thousands, date format).'
                        : `${pendingFile ? `Parsing ${pendingFile.name}` : 'Parsing pasted data'}. Add instructions for the AI or leave blank to parse as-is.`}
                    </p>
                    <textarea
                      value={promptPopupValue}
                      onChange={(e) => setPromptPopupValue(e.target.value)}
                      placeholder={isRetryPopup ? 'e.g. Amounts are in thousands, multiply by 1000. Dates are DD/MM/YYYY.' : 'e.g. First column is phase name, second is value in NZD. Skip header row.'}
                      rows={3}
                      className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm placeholder-gray-500 mb-4"
                      autoFocus
                    />
                    <div className="flex justify-end gap-3">
                      <button
                        type="button"
                        onClick={handleCancelPromptPopup}
                        className="px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleConfirmPromptPopup}
                        className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700"
                      >
                        {isRetryPopup ? 'Retry' : 'Parse'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between pt-6 mt-4 border-t border-gray-200 dark:border-gray-600">
            <div>
              {step === 1 ? (
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  className="inline-flex items-center gap-2 px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
            </div>
            <div>
              {step === 1 ? (
                <button
                  type="submit"
                  form="create-project-step1-form"
                  disabled={!canProceedStep1}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : step === 2 && retentionEnabled ? (
                <button
                  type="submit"
                  form="create-project-step2-form"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="submit"
                  form={step === 3 ? 'create-project-step3-form' : 'create-project-step2-form'}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50"
                >
                  {submitting ? 'Creating...' : 'Create'}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
